import { Broadcast, Fan, SquareHalf, ThermometerHot, Wind } from "@phosphor-icons/react";
import { ArrowRight } from "lucide-react";
import { useState } from "react";

import { logger } from "@/lib/logger";

import {
  AppBar,
  HelpHint,
  type Material,
  MaterialsNeeded,
  PhaseDots,
  PillButton,
  StepList,
} from "./components";
import { PRODUCT_NAMES } from "./product-names";

/**
 * Step 1 · SmartClimate Hub — the module intro and network prep, before the hub
 * connects. Figma 901:698 (intro), 1052:1945 (power / power strip), 900:874
 * (check router LAN ports), 1150:984 (switch branch).
 *
 * Sequence: the module intro (why this part, plus the parts to have to hand) →
 * plug in the power strip so everything has power → check the router for free
 * LAN ports. With 3+ free the user goes straight to connecting; with 2 or fewer
 * the kit's switch is set up first. The choice is stored in the step payload.
 * These are dots 1–3 of the five-dot "Step 1" sequence; the hub-setup screen
 * continues with dots 4–5.
 *
 * The 2026-07-27 redesign inverted the hierarchy on these screens: the heading
 * and explanation come first and the illustration sits below them, so the user
 * reads what to do before seeing it.
 */

/**
 * Shared header for the whole "Step 1 · SmartClimate Hub" sequence (these
 * screens plus the hub-setup screen). The bar names the module; each screen's
 * own heading says what to do on it.
 */
export const STEP_TITLE = PRODUCT_NAMES.hub;
export const HUB_STEP_TOTAL = 5;

/** Figma's 358px content column inside the 390px frame. */
const COLUMN = "px-4";

const SWITCH_STEPS = [
  "Take the network switch and a white cable from compartment 1.",
  "Plug the white cable into any free LAN port on your router.",
  "Plug the other end into any port on the switch, they all work.",
  "Plug the switch's power cable into your power strip.",
];

/**
 * The parts to have ready before starting, each with the photo box Figma gives
 * it (1064:850…1064:865) — a cable reads at a different size to a switch.
 */
const HUB_MATERIALS: Material[] = [
  {
    title: PRODUCT_NAMES.hub,
    caption: "Comes with a power cord.",
    img: "/brand/onboarding/hub-kit-hub.png",
    alt: `The ${PRODUCT_NAMES.hub}`,
    imgWidth: 95,
    imgHeight: 55,
  },
  {
    title: PRODUCT_NAMES.hubSwitch,
    caption: "Only if your router is short on ports. Comes with a power cord.",
    img: "/brand/onboarding/hub-kit-switch.png",
    alt: "A network switch",
    imgWidth: 97,
    imgHeight: 36,
  },
  {
    title: "White ethernet cable",
    img: "/brand/onboarding/hub-kit-ethernet.png",
    alt: "A white ethernet cable",
    imgWidth: 65,
    imgHeight: 48,
  },
  {
    title: "Power strip",
    img: "/brand/onboarding/hub-kit-multioutlet.png",
    alt: "A power strip",
    imgWidth: 84,
    imgHeight: 42,
  },
];

/** Satellite pucks orbiting the hub — Figma 1130:850…1131:861. */
const SATELLITES = [
  { key: "windows", Icon: SquareHalf, box: 50, icon: 24, left: 66, top: 17 },
  { key: "ventilation", Icon: Wind, box: 66, icon: 36, left: 251, top: 47 },
  { key: "heating", Icon: ThermometerHot, box: 66, icon: 31, left: 33, top: 190 },
  { key: "air", Icon: Fan, box: 50, icon: 24, left: 234, top: 206 },
];

/** The art box Figma drew the constellation in; everything below is a share of it. */
const ART_W = 326;
const ART_H = 291;
const pct = (value: number, total: number) => `${(value / total) * 100}%`;

/**
 * Hero for the module intro — Figma 1130:854. Two hairline orbits around a
 * central hub puck, with the four things it coordinates around it. Built from
 * the same Phosphor light glyphs the design uses rather than a flat export, so
 * it stays sharp at any width and follows the colour tokens.
 */
function HubConstellation() {
  return (
    <div className="relative w-full" style={{ aspectRatio: `${ART_W}/${ART_H}` }} aria-hidden>
      {[264, 186].map((diameter) => (
        <div
          key={diameter}
          // One step darker than `border-subtle` now that the constellation
          // sits on the hub's Residential-Grey block rather than page white —
          // the hairline orbits kept the same weight against the tint.
          className="border-border absolute rounded-full border"
          style={{
            width: pct(diameter, ART_W),
            height: pct(diameter, ART_H),
            left: pct(165, ART_W),
            top: pct(144, ART_H),
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}
      {SATELLITES.map(({ key, Icon, box, icon, left, top }) => (
        <div
          key={key}
          className="absolute grid place-items-center rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)]"
          style={{
            width: pct(box, ART_W),
            height: pct(box, ART_H),
            left: pct(left, ART_W),
            top: pct(top, ART_H),
          }}
        >
          <Icon
            weight="light"
            className="text-muted"
            style={{ width: pct(icon, box), height: pct(icon, box) }}
          />
        </div>
      ))}
      <div
        className="absolute grid place-items-center rounded-full bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)]"
        style={{
          width: pct(106, ART_W),
          height: pct(106, ART_H),
          left: pct(112, ART_W),
          top: pct(91, ART_H),
        }}
      >
        <Broadcast
          weight="light"
          className="text-muted"
          style={{ width: pct(61, 106), height: pct(61, 106) }}
        />
      </div>
    </div>
  );
}

interface IllustrationProps {
  src: string;
  alt: string;
  /** Figma frames these at 1536/1024; the switch/hub artwork is 1448/1086. */
  aspect?: string;
}

/** Full-bleed instructional illustration, sitting under its heading. */
function Illustration({ src, alt, aspect = "1536/1024" }: IllustrationProps) {
  return (
    <img src={src} alt={alt} className="w-full object-cover" style={{ aspectRatio: aspect }} />
  );
}

/**
 * Every stage of this module, in order — the dev screen gallery walks it, and
 * `Stage` is derived from it so the two can never disagree. Add a stage here
 * and it is in the gallery; there is no second list to remember.
 *
 * `ready` and `switch` are the two branches of the port question, so they share
 * a dot: which one you land on is the answer, not progress.
 */
export const STAGES = ["intro", "power", "check", "ready", "switch"] as const;

export type Stage = (typeof STAGES)[number];

interface NetworkSetupScreenProps {
  busy: boolean;
  error: string;
  onComplete: (payload: Record<string, unknown>) => void;
  /**
   * Stage to open on. Production always omits it (each module starts at its
   * own first stage); the dev screen gallery uses it to deep-link a stage.
   */
  initialStage?: Stage;
}

export function NetworkSetupScreen({
  busy,
  error,
  onComplete,
  initialStage,
}: NetworkSetupScreenProps) {
  const [stage, setStage] = useState<Stage>(initialStage ?? "intro");

  // ── Dot 1 · module intro: why this part, and what to have ready ──────────
  if (stage === "intro") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar stepKey="network_setup" stage={stage} title={STEP_TITLE} />
        <PhaseDots total={HUB_STEP_TOTAL} activeIndex={0} />
        <div className="flex flex-1 flex-col gap-5 px-7 pt-5">
          {/* The hub's flat module block — Residential Grey, the neutral of the
              tint map (see globals.css). Every other module intro shows its
              product on a coloured block; this one drew its constellation
              straight onto the page white, which left compartment 1 as the only
              intro without one. */}
          <div className="bg-surface-subtle flex justify-center rounded-2xl px-6 py-7">
            {/* Same 200px the constellation rendered at before the block —
                `w-full` under a max, rather than a fraction of the narrower
                padded box, so it did not quietly shrink. */}
            <div className="w-full max-w-[200px]">
              <HubConstellation />
            </div>
          </div>
          <h1 className="text-primary type-title">Setting up your {PRODUCT_NAMES.hub}</h1>
          <p className="text-muted text-[17px] leading-6">
            <span className="font-bold">Why this part?</span> The {PRODUCT_NAMES.hub} is the brain
            of your system. Everything else in the box connects through it.
          </p>
          <MaterialsNeeded items={HUB_MATERIALS} />
          <p className="text-primary text-[17px] leading-6">
            Make sure you have everything ready before we start.
          </p>
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-7">
          <PillButton label="I have everything" onClick={() => setStage("power")} />
        </div>
      </div>
    );
  }

  // ── Dot 2 · power: plug in the power strip near the router ──────────────
  if (stage === "power") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="network_setup"
          stage={stage}
          title={STEP_TITLE}
          onBack={() => setStage("intro")}
        />
        <PhaseDots total={HUB_STEP_TOTAL} activeIndex={1} />
        <div className="flex flex-1 flex-col gap-3 px-[18px] pt-8">
          <h1 className="text-primary type-title">
            Plug in the power strip near your Wi-Fi router
          </h1>
          <p className="text-muted text-[17px] leading-6">
            It&apos;s in compartment 1. Everything you connect next needs power here.
          </p>
          <Illustration
            src="/brand/onboarding/hub-power-outlet.png"
            alt="A power strip plugged into a wall socket beside a Wi-Fi router"
          />
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-8">
          <PillButton label="It's plugged in" onClick={() => setStage("check")} />
        </div>
      </div>
    );
  }

  // ── Dot 3 · check the router for free LAN ports (branch) ─────────────────
  if (stage === "check") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="network_setup"
          stage={stage}
          title={STEP_TITLE}
          onBack={() => setStage("power")}
        />
        <PhaseDots total={HUB_STEP_TOTAL} activeIndex={2} />
        <div className={`flex flex-1 flex-col gap-8 pt-8 pb-7 ${COLUMN}`}>
          <div className="flex flex-col gap-4">
            <h1 className="text-primary type-title">
              How many free LAN ports does your Wi-Fi router have?
            </h1>
            <p className="text-muted text-[17px] leading-6">
              Look at the back. Ignore the port marked Internet, that one&apos;s in use.
            </p>
            <Illustration
              src="/brand/onboarding/hub-router-lan.png"
              alt="The back of a Wi-Fi router with its four free LAN ports highlighted"
            />
          </div>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => {
                logger.debug("[onboarding] network: enough LAN ports");
                setStage("ready");
              }}
              className="border-border hover:border-sky flex min-h-[110px] flex-col justify-end gap-1 rounded-2xl border-2 bg-white p-3 text-left transition-colors disabled:opacity-60"
            >
              <span className="text-primary flex items-center gap-1 text-[15px] leading-[22px]">
                I have 3 or more free LAN ports <ArrowRight className="size-4" />
              </span>
              <span className="text-muted type-caption">Straight to connecting</span>
            </button>
            <button
              type="button"
              onClick={() => setStage("switch")}
              className="border-border hover:border-sky flex min-h-[110px] flex-col justify-end gap-1 rounded-2xl border-2 bg-white p-3 text-left transition-colors"
            >
              <span className="text-primary flex items-center gap-1 text-[15px] leading-[22px]">
                I have 2 or fewer free <ArrowRight className="size-4" />
              </span>
              <span className="text-muted type-caption">
                No problem, your kit includes a switch
              </span>
            </button>
          </div>
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Dot 3 (branch) · enough ports, so nothing to wire ───────────────────
  //
  // This screen exists to make the port answer reversible. It used to complete
  // the step on the tap, and because `network_setup` has no row of its own on
  // the guide, a miscount was permanent: the flow moved to the hub and there
  // was no way back to say "I counted wrong". Both answers now land on a screen
  // with a back arrow, and the step completes from the CTA.
  if (stage === "ready") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="network_setup"
          stage={stage}
          title={STEP_TITLE}
          onBack={() => setStage("check")}
        />
        <PhaseDots total={HUB_STEP_TOTAL} activeIndex={2} />
        <div className={`flex flex-1 flex-col gap-8 pt-8 ${COLUMN}`}>
          <div className="flex flex-col gap-4">
            <h1 className="text-primary type-title">Your router has the ports we need</h1>
            <p className="text-muted text-[17px] leading-6">
              Nothing to wire at this step. The {PRODUCT_NAMES.hubSwitch} in your kit stays unused,
              so leave it in its compartment.
            </p>
            <Illustration
              src="/brand/onboarding/hub-router-lan.png"
              alt="The back of a Wi-Fi router with its four free LAN ports highlighted"
            />
          </div>
          <HelpHint
            question="Counted wrong?"
            answer="Use the arrow at the top to go back and change your answer. Your kit includes a switch if you need one."
          />
        </div>
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-8">
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton
            label="Continue"
            busy={busy}
            onClick={() => {
              logger.debug("[onboarding] network: no switch needed");
              onComplete({ needs_switch: false });
            }}
          />
        </div>
      </div>
    );
  }

  // ── Dot 3 (branch) · connect the network switch ──────────────────────────
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <AppBar
        stepKey="network_setup"
        stage={stage}
        title={STEP_TITLE}
        onBack={() => setStage("check")}
      />
      <PhaseDots total={HUB_STEP_TOTAL} activeIndex={2} />
      <div className={`flex flex-1 flex-col gap-8 pt-8 ${COLUMN}`}>
        <div className="flex flex-col gap-4">
          <h1 className="text-primary type-title">
            That&apos;s why there&apos;s a switch in your kit
          </h1>
          <p className="text-muted text-[17px] leading-6">
            Your router is short on ports, so we packed a network switch. It turns one LAN port into
            four.
          </p>
          {/* Figma labels the two devices on the artwork itself (1150:1055/1056). */}
          <div className="relative w-full">
            <Illustration
              src="/brand/onboarding/hub-switch-wiring.png"
              alt="A white cable running from the router's LAN port to the network switch, with the switch powered from the power strip"
            />
            <span className="text-muted type-caption absolute top-[12.6%] left-[16.5%]">
              Your Router
            </span>
            <span className="text-muted type-caption absolute top-[27.7%] left-[60.3%]">
              {PRODUCT_NAMES.hubSwitch}
            </span>
          </div>
        </div>
        <StepList steps={SWITCH_STEPS} />
        <HelpHint
          question="No free LAN port at all?"
          answer="Unplug any one cable from the router, plug it into the switch, then connect the switch to the port you just freed. Everything keeps working."
        />
      </div>
      <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-8">
        {error && (
          <p role="alert" className="text-destructive text-center text-[15px]">
            {error}
          </p>
        )}
        <PillButton
          label="Network switch lights are on"
          busy={busy}
          onClick={() => {
            logger.debug("[onboarding] network: switch connected");
            onComplete({ needs_switch: true });
          }}
        />
      </div>
    </div>
  );
}
